import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import type { Document } from '@contentful/rich-text-types';
import SmartLink from './SmartLink';
import CFImage from './CFImage';
import { rewriteAssetUrl } from '@/lib/contentful/rewriteAssetUrl';
import type { Asset } from 'contentful';

/** Long Danish words / titles must wrap inside narrow viewports (WCAG 1.4.4 / 1.4.10). */
const richWrap = 'min-w-0 max-w-full';
const headingWrap =
  `${richWrap} break-words hyphens-none leading-tight [overflow-wrap:anywhere]`;

const options: Options = {
  renderMark: {
    [MARKS.BOLD]: (text) => <span className="contents font-semibold">{text}</span>,
    [MARKS.ITALIC]: (text) => <span className="contents italic">{text}</span>,
    [MARKS.CODE]: (text) => <code className="rounded bg-black/10 px-1 py-0.5 font-mono text-sm">{text}</code>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <p className={`mb-4 last:mb-0 ${richWrap} break-words`}>{children}</p>
    ),
    [BLOCKS.HEADING_1]: (node, children) => (
      <h1 className={`mb-4 text-5xl font-bold ${headingWrap}`}>{children}</h1>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <h2 className={`mb-3 text-4xl font-bold ${headingWrap}`}>{children}</h2>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <h3 className={`mb-3 text-2xl font-semibold ${headingWrap}`}>{children}</h3>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <h4 className={`mb-2 text-xl font-semibold ${headingWrap}`}>{children}</h4>
    ),
    [BLOCKS.UL_LIST]: (node, children) => <ul className="mb-4 list-disc pl-6">{children}</ul>,
    [BLOCKS.OL_LIST]: (node, children) => <ol className="mb-4 list-decimal pl-6">{children}</ol>,
    [BLOCKS.LIST_ITEM]: (node, children) => <li className="mb-1">{children}</li>,
    [BLOCKS.QUOTE]: (node, children) => (
      <blockquote className="mb-4 border-l-4 border-gray-300 pl-4 italic text-gray-600">{children}</blockquote>
    ),
    [BLOCKS.HR]: () => <hr className="my-6 border-gray-200" />,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const asset = node.data.target as Asset;
      if (!asset?.fields?.file) return null;
      return (
        <figure className="my-6">
          <CFImage asset={asset} className="w-full h-auto rounded" sizes="(max-width: 768px) 100vw, 700px" />
        </figure>
      );
    },
    [INLINES.HYPERLINK]: (node, children) => (
      <SmartLink href={rewriteAssetUrl(node.data.uri)} className="text-primary underline hover:no-underline">
        {children}
      </SmartLink>
    ),
    [INLINES.ASSET_HYPERLINK]: (node, children) => {
      const asset = node.data.target as Asset;
      const file = (asset?.fields as { file?: { url?: string } })?.file;
      if (!file?.url) return <>{children}</>;
      const url = file.url.startsWith('//') ? `https:${file.url}` : file.url;
      return (
        <a href={rewriteAssetUrl(url)} className="text-primary underline hover:no-underline">
          {children}
        </a>
      );
    },
  },
};

interface RichTextProps {
  document: Document;
  className?: string;
}

export default function RichText({ document, className = '' }: RichTextProps) {
  return (
    <div className={`${richWrap} w-full ${className}`.trim()}>
      {documentToReactComponents(document, options)}
    </div>
  );
}
